import React, { useMemo, useRef } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';

import { DataCell, DeleteCheckboxCell, EditableCell, RowIndexCell } from './BulkUploadTableCells';

/** Above this row count, only render visible rows using padding-row virtualization. */
const VIRTUALIZE_ROW_THRESHOLD = 50;

const ROW_HEIGHT_ESTIMATE_PX = 48;

const createColumns = (columnDefs, editable, deletable) => {
    const CellComponent = editable ? EditableCell : DataCell;

    const cols = columnDefs.map((def) => ({
        id: def.key,
        accessorFn: (row) => row.data[def.key],
        header: def.label,
        cell: def.cell ?? CellComponent,
    }));

    cols.unshift({
        id: '_rowIndex',
        header: '#',
        cell: RowIndexCell,
        size: 50,
    });

    if (deletable) {
        cols.unshift({
            id: '_delete',
            header: '',
            cell: DeleteCheckboxCell,
            size: 50,
        });
    }

    return cols;
};

/**
 * Table for displaying bulk upload data with validation errors, inline editing, and row deletion.
 *
 * @param {Object} props
 * @param {Array<{ key: string, label: string, cell?: import('@tanstack/react-table').ColumnDefTemplate<import('@tanstack/react-table').CellContext<{ rowIndex: number, data: Object }, unknown>> }>} props.columnDefs - Column definitions
 * @param {Array<{ rowIndex: number, data: Object }>} props.rows - Row data from the API
 * @param {Map<string, { row: number, field: string, message: string }>} props.errorsByCell - Error lookup map
 * @param {Object} [props.pendingEdits] - Map of pending edits by rowIndex -> { field: value }
 * @param {Function} [props.updateCell] - Callback to update a cell value: (rowIndex, field, value) => void
 * @param {Set<number>} [props.pendingDeletes] - Set of row indices marked for deletion
 * @param {Function} [props.toggleRowDelete] - Callback to toggle row deletion: (rowIndex) => void
 * @param {string} [props.className] - Additional classes
 * @param {boolean} [props.fillAvailableHeight] - When true, scroll area grows in a flex parent (no fixed max-height)
 */
export function BulkUploadTable({
    className,
    columnDefs,
    errorsByCell,
    fillAvailableHeight = false,
    pendingDeletes,
    pendingEdits,
    rows,
    toggleRowDelete,
    updateCell,
}) {
    const editable = Boolean(updateCell);
    const deletable = Boolean(toggleRowDelete);

    // Memoize columns based on columnDefs, editable, and deletable mode
    const columns = useMemo(() => createColumns(columnDefs, editable, deletable), [columnDefs, editable, deletable]);

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => String(row.rowIndex),
        meta: {
            errorsByCell,
            pendingDeletes,
            pendingEdits,
            toggleRowDelete,
            updateCell,
        },
    });

    const scrollParentRef = useRef(null);
    const tableRowsModel = table.getRowModel().rows;
    const shouldVirtualize = tableRowsModel.length > VIRTUALIZE_ROW_THRESHOLD;

    const rowVirtualizer = useVirtualizer({
        count: shouldVirtualize ? tableRowsModel.length : 0,
        enabled: shouldVirtualize,
        estimateSize: () => ROW_HEIGHT_ESTIMATE_PX,
        getScrollElement: () => scrollParentRef.current,
        initialRect: { height: 640, width: 1024 },
        overscan: 8,
    });

    const virtualRows = shouldVirtualize ? rowVirtualizer.getVirtualItems() : [];
    const totalSize = shouldVirtualize ? rowVirtualizer.getTotalSize() : 0;
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

    const headerGroup = table.getHeaderGroups()[0];
    const colCount = headerGroup?.headers.length ?? 0;

    if (rows.length === 0) {
        return (
            <div className={cn('rounded-lg border p-8 text-center text-muted-foreground', className)}>
                No data to display
            </div>
        );
    }

    const renderDataRow = (row) => (
        <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="text-center">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
            ))}
        </TableRow>
    );

    return (
        <div className={cn('rounded-lg border', fillAvailableHeight && 'flex min-h-0 flex-1 flex-col', className)}>
            <div
                ref={scrollParentRef}
                className={cn(
                    fillAvailableHeight ? 'min-h-0 flex-1 overflow-auto' : 'max-h-[min(70vh,600px)] overflow-auto',
                )}
                data-testid="bulk-upload-table-scroll"
            >
                {/* Outer scroll div handles x+y; Table defaults to overflow-x-auto which nested a short scrollbox. */}
                <Table className="overflow-x-visible">
                    <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
                        {table.getHeaderGroups().map((hg) => (
                            <TableRow key={hg.id}>
                                {hg.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="text-center"
                                        style={{
                                            width:
                                                header.column.getSize() !== 150 ? header.column.getSize() : undefined,
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {shouldVirtualize ? (
                            <>
                                {paddingTop > 0 && (
                                    <tr aria-hidden="true">
                                        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- spacer cell */}
                                        <td
                                            colSpan={colCount}
                                            style={{ height: `${paddingTop}px`, padding: 0, border: 0, lineHeight: 0 }}
                                        />
                                    </tr>
                                )}
                                {virtualRows.map((virtualRow) => {
                                    const row = tableRowsModel[virtualRow.index];
                                    return (
                                        <TableRow key={row.id} style={{ height: `${virtualRow.size}px` }}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id} className="text-center">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                                {paddingBottom > 0 && (
                                    <tr aria-hidden="true">
                                        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- spacer cell */}
                                        <td
                                            colSpan={colCount}
                                            style={{
                                                height: `${paddingBottom}px`,
                                                padding: 0,
                                                border: 0,
                                                lineHeight: 0,
                                            }}
                                        />
                                    </tr>
                                )}
                            </>
                        ) : (
                            tableRowsModel.map((row) => renderDataRow(row))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
